import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  Badge,
  useColorModeValue,
  Spinner,
  Center,
  VStack,
  HStack,
  Divider,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import {
  CalendarIcon,
  InfoIcon,
  CheckCircleIcon,
  WarningIcon,
  TimeIcon,
} from "@chakra-ui/icons";
import axios from "axios";
import { decryptData, URL_WEB_BACKEND } from "CustomUtil";
import Card from "components/card/Card.js";

export default function UpTable() {
  const authdata = decryptData(localStorage.getItem("authinfosemakinpas"));
  const namanya = decryptData(localStorage.getItem("authfullnamesemakinpas"));
  const userid = decryptData(localStorage.getItem("authidsemakinpas"));

  const [loading, setLoading] = useState(true);
  const [kepegawaian, setKepegawaian] = useState(null);
  const [error, setError] = useState(null);

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("gray.600", "gray.400");
  const bgCard = useColorModeValue("white", "gray.800");
  const bgInfo = useColorModeValue("blue.50", "blue.900");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  useEffect(() => {
    fetchKepegawaian();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchKepegawaian = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${URL_WEB_BACKEND}/kepegawaians/${userid}`,
        {
          headers: {
            Authorization: "Bearer " + authdata,
          },
        }
      );
      setKepegawaian(res.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response && err.response.status === 404) {
        setError("Data Anda belum tersedia. Silakan hubungi admin.");
      } else {
        setError("Gagal memuat data.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk menghitung kenaikan pangkat
  const hitungKenaikanPangkat = () => {
    if (!kepegawaian.tmt_pangkat_terakhir) {
      return {
        status: 'no_data',
        message: 'TMT Pangkat belum diisi',
        color: 'gray',
        icon: InfoIcon
      };
    }

    const today = new Date();
    const tmtPangkat = new Date(kepegawaian.tmt_pangkat_terakhir);

    // Hitung penundaan hukdis
    const penundaanBulan = (kepegawaian.hukdis && kepegawaian.lama_hukdis)
      ? parseInt(kepegawaian.lama_hukdis)
      : 0;

    // Hitung umur jabatan dalam bulan
    const diffTime = today - tmtPangkat;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    const umurBulan = Math.floor(diffDays / 30);
    const umurTahun = Math.floor(umurBulan / 12);
    const sisaBulan = umurBulan % 12;

    // Threshold: 3 tahun 10 bulan + penundaan hukdis
    const thresholdBulan = (3 * 12) + 10 + penundaanBulan;

    // Deadline: 4 tahun + penundaan hukdis
    const deadlineDate = new Date(tmtPangkat);
    deadlineDate.setFullYear(deadlineDate.getFullYear() + 4);
    deadlineDate.setMonth(deadlineDate.getMonth() + penundaanBulan);

    const deadlineStr = deadlineDate.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // Hitung sisa waktu ke deadline
    const sisaWaktu = deadlineDate - today;
    const sisaHari = Math.floor(sisaWaktu / (1000 * 60 * 60 * 24));
    const sisaTahun = Math.floor(sisaHari / 365);
    const sisaBulanHitung = Math.floor((sisaHari % 365) / 30);
    const sisaHariHitung = sisaHari % 30;

    let status, message, color, icon, detail, actionMessage;

    if (umurBulan < thresholdBulan) {
      // Belum saatnya
      const kurangBulan = thresholdBulan - umurBulan;
      const kurangTahun = Math.floor(kurangBulan / 12);
      const kurangSisaBulan = kurangBulan % 12;

      status = 'belum_saatnya';
      message = 'Belum Saatnya Naik Pangkat';
      color = 'blue';
      icon = TimeIcon;
      detail = `Masih ${kurangTahun} tahun ${kurangSisaBulan} bulan lagi`;
      actionMessage = 'Anda belum memenuhi syarat masa kerja untuk kenaikan pangkat.';
    } else if (deadlineDate > today) {
      // Sudah bisa diajukan, belum lewat deadline
      status = 'segera';
      message = '🎉 Anda Sudah Bisa Naik Pangkat!';
      color = 'green';
      icon = CheckCircleIcon;
      detail = `Deadline: ${sisaTahun} tahun ${sisaBulanHitung} bulan ${sisaHariHitung} hari lagi`;
      actionMessage = 'Segera hubungi admin/atasan untuk memproses kenaikan pangkat Anda!';
    } else {
      // Sudah lewat deadline
      const lewatHari = Math.abs(sisaHari);
      const lewatTahun = Math.floor(lewatHari / 365);
      const lewatBulan = Math.floor((lewatHari % 365) / 30);
      const lewatSisaHari = lewatHari % 30;

      status = 'terlambat';
      message = '⚠️ Kenaikan Pangkat Terlambat!';
      color = 'red';
      icon = WarningIcon;
      detail = `Terlambat ${lewatTahun} tahun ${lewatBulan} bulan ${lewatSisaHari} hari`;
      actionMessage = 'SEGERA hubungi admin/atasan untuk memproses kenaikan pangkat Anda!';
    }

    return {
      status,
      message,
      color,
      icon,
      detail,
      deadlineStr,
      umurJabatan: `${umurTahun} tahun ${sisaBulan} bulan`,
      penundaanBulan,
      adaHukdis: penundaanBulan > 0,
      actionMessage
    };
  };

  // Fungsi untuk menghitung kenaikan gaji berkala
  const hitungKenaikanGaji = () => {
    if (!kepegawaian.tmt_gaji_terakhir) {
      return {
        status: 'no_data',
        message: 'TMT Gaji belum diisi',
        color: 'gray',
        icon: InfoIcon
      };
    }

    const today = new Date();
    const tmtGaji = new Date(kepegawaian.tmt_gaji_terakhir);

    // Hitung umur gaji dalam bulan
    const diffTime = today - tmtGaji;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    const umurBulan = Math.floor(diffDays / 30);
    const umurTahun = Math.floor(umurBulan / 12);
    const sisaBulan = umurBulan % 12;

    // Threshold: 1 tahun 10 bulan
    const thresholdBulan = (1 * 12) + 10;

    // Deadline: 2 tahun
    const deadlineDate = new Date(tmtGaji);
    deadlineDate.setFullYear(deadlineDate.getFullYear() + 2);

    const deadlineStr = deadlineDate.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // Hitung sisa waktu ke deadline
    const sisaWaktu = deadlineDate - today;
    const sisaHari = Math.floor(sisaWaktu / (1000 * 60 * 60 * 24));
    const sisaTahun = Math.floor(sisaHari / 365);
    const sisaBulanHitung = Math.floor((sisaHari % 365) / 30);
    const sisaHariHitung = sisaHari % 30;

    let status, message, color, icon, detail, actionMessage;

    if (umurBulan < thresholdBulan) {
      // Belum saatnya
      const kurangBulan = thresholdBulan - umurBulan;
      const kurangTahun = Math.floor(kurangBulan / 12);
      const kurangSisaBulan = kurangBulan % 12;

      status = 'belum_saatnya';
      message = 'Belum Saatnya KGB';
      color = 'blue';
      icon = TimeIcon;
      detail = `Masih ${kurangTahun} tahun ${kurangSisaBulan} bulan lagi`;
      actionMessage = 'Anda belum memenuhi syarat masa kerja untuk KGB.';
    } else if (deadlineDate > today) {
      // Sudah bisa diajukan, belum lewat deadline
      status = 'segera';
      message = '🎉 Anda Sudah Bisa Naik Gaji Berkala!';
      color = 'green';
      icon = CheckCircleIcon;
      detail = `Deadline: ${sisaTahun} tahun ${sisaBulanHitung} bulan ${sisaHariHitung} hari lagi`;
      actionMessage = 'Segera hubungi admin/atasan untuk memproses KGB Anda!';
    } else {
      // Sudah lewat deadline
      const lewatHari = Math.abs(sisaHari);
      const lewatTahun = Math.floor(lewatHari / 365);
      const lewatBulan = Math.floor((lewatHari % 365) / 30);
      const lewatSisaHari = lewatHari % 30;

      status = 'terlambat';
      message = '⚠️ KGB Terlambat!';
      color = 'red';
      icon = WarningIcon;
      detail = `Terlambat ${lewatTahun} tahun ${lewatBulan} bulan ${lewatSisaHari} hari`;
      actionMessage = 'SEGERA hubungi admin/atasan untuk memproses KGB Anda!';
    }

    return {
      status,
      message,
      color,
      icon,
      detail,
      deadlineStr,
      umurGaji: `${umurTahun} tahun ${sisaBulan} bulan`,
      actionMessage
    };
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Card p={6}>
          <Center h="400px">
            <VStack>
              <Spinner
                thickness="4px"
                speed="0.95s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
              <Text mt={4} color={textColorSecondary}>
                Memuat data...
              </Text>
            </VStack>
          </Center>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Card p={6}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Data Tidak Tersedia</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
        </Card>
      </Box>
    );
  }

  const infoPangkat = hitungKenaikanPangkat();
  const infoGaji = hitungKenaikanGaji();

  return (
    <Box>
      {/* Header */}
      <Card mb={4} p={6}>
        <Flex direction="column">
          <HStack justify="space-between" mb={2}>
            <Text fontSize="2xl" fontWeight="bold" color={textColorPrimary}>
              Profil Saya
            </Text>
            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
              Petugas
            </Badge>
          </HStack>
          <Text fontSize="lg" color={textColorPrimary}>
            {namanya?.toUpperCase()}
          </Text>
        </Flex>
      </Card>

      {/* Alert Notifikasi Jika Sudah Saatnya */}
      {(infoPangkat.status === 'segera' || infoPangkat.status === 'terlambat' ||
        infoGaji.status === 'segera' || infoGaji.status === 'terlambat') && (
          <Alert
            status={
              infoPangkat.status === 'terlambat' || infoGaji.status === 'terlambat'
                ? "error"
                : "success"
            }
            mb={4}
            borderRadius="md"
          >
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>
                {infoPangkat.status === 'terlambat' || infoGaji.status === 'terlambat'
                  ? '⚠️ Perhatian!'
                  : '🎉 Selamat!'}
              </AlertTitle>
              <AlertDescription>
                {infoPangkat.status === 'segera' && infoGaji.status === 'segera' && (
                  <>Anda sudah memenuhi syarat untuk <strong>Kenaikan Pangkat</strong> dan <strong>KGB</strong>. Segera hubungi atasan Anda! </>
                )}
                {infoPangkat.status === 'segera' && infoGaji.status !== 'segera' && (
                  <>Anda sudah memenuhi syarat untuk <strong>Kenaikan Pangkat</strong>. Segera hubungi atasan Anda! </>
                )}
                {infoGaji.status === 'segera' && infoPangkat.status !== 'segera' && (
                  <>Anda sudah memenuhi syarat untuk <strong>Kenaikan Gaji Berkala (KGB)</strong>. Segera hubungi atasan Anda! </>
                )}
                {(infoPangkat.status === 'terlambat' || infoGaji.status === 'terlambat') && (
                  <>Pengajuan kenaikan Anda sudah melewati batas waktu. <strong>SEGERA</strong> hubungi atasan! </>
                )}
              </AlertDescription>
            </Box>
          </Alert>
        )}

      {/* Rincian Data */}
      <Card mb={4} p={6}>
        <Text fontSize="xl" fontWeight="bold" color={textColorPrimary} mb={4}>
          Rincian Data
        </Text>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          <GridItem>
            <HStack justify="space-between" py={2} borderBottom="1px" borderColor={borderColor}>
              <Text fontSize="sm" color={textColorSecondary}>Jenis Kelamin:</Text>
              <Text fontSize="sm" fontWeight="medium" color={textColorPrimary}>
                {kepegawaian.jenis_kelamin === 'L' ? 'Laki-laki' : 'Perempuan'}
              </Text>
            </HStack>
          </GridItem>

          <GridItem>
            <HStack justify="space-between" py={2} borderBottom="1px" borderColor={borderColor}>
              <Text fontSize="sm" color={textColorSecondary}>Pendidikan Terakhir:</Text>
              <Text fontSize="sm" fontWeight="medium" color={textColorPrimary}>
                {kepegawaian.pendidikan_terakhir || '-'}
              </Text>
            </HStack>
          </GridItem>

          <GridItem>
            <HStack justify="space-between" py={2} borderBottom="1px" borderColor={borderColor}>
              <Text fontSize="sm" color={textColorSecondary}>Pangkat / Golongan:</Text>
              <Text fontSize="sm" fontWeight="medium" color={textColorPrimary}>
                {kepegawaian.pangkat_golongan || '-'}
              </Text>
            </HStack>
          </GridItem>

          <GridItem>
            <HStack justify="space-between" py={2} borderBottom="1px" borderColor={borderColor}>
              <Text fontSize="sm" color={textColorSecondary}>Jabatan:</Text>
              <Text fontSize="sm" fontWeight="medium" color={textColorPrimary}>
                {kepegawaian.jabatan || '-'}
              </Text>
            </HStack>
          </GridItem>

          <GridItem>
            <HStack justify="space-between" py={2} borderBottom="1px" borderColor={borderColor}>
              <Text fontSize="sm" color={textColorSecondary}>Eselon:</Text>
              <Text fontSize="sm" fontWeight="medium" color={textColorPrimary}>
                {kepegawaian.eselon || '-'}
              </Text>
            </HStack>
          </GridItem>

          {kepegawaian.hukdis && kepegawaian.lama_hukdis > 0 && (
            <>
              <GridItem>
                <HStack justify="space-between" py={2} borderBottom="1px" borderColor={borderColor}>
                  <Text fontSize="sm" color={textColorSecondary}>Hukuman Disiplin:</Text>
                  <Badge colorScheme="red" fontSize="xs">
                    {kepegawaian.hukdis}
                  </Badge>
                </HStack>
              </GridItem>
              <GridItem>
                <HStack justify="space-between" py={2} borderBottom="1px" borderColor={borderColor}>
                  <Text fontSize="sm" color={textColorSecondary}>Lama Hukdis:</Text>
                  <Badge colorScheme="red" fontSize="xs">
                    {kepegawaian.lama_hukdis} bulan
                  </Badge>
                </HStack>
              </GridItem>
            </>
          )}
        </Grid>
      </Card>

      {/* Informasi Kenaikan Pangkat dan Gaji */}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
        {/* Card Kenaikan Pangkat */}
        <GridItem>
          <Card
            p={6}
            border="2px solid"
            borderColor={infoPangkat.status === 'terlambat' ? 'red.400' :
              infoPangkat.status === 'segera' ? 'green.400' :
                infoPangkat.status === 'belum_saatnya' ? 'blue.400' : borderColor}
          >
            <HStack justify="space-between" mb={4}>
              <HStack>
                <Icon as={CalendarIcon} boxSize={6} color="orange.500" />
                <Text fontSize="lg" fontWeight="bold" color={textColorPrimary}>
                  Kenaikan Pangkat
                </Text>
              </HStack>
              <Badge colorScheme={infoPangkat.color} fontSize="sm" px={3} py={1}>
                <Icon as={infoPangkat.icon} mr={1} />
                {infoPangkat.status === 'no_data' ? 'NO DATA' :
                  infoPangkat.status === 'terlambat' ? 'TERLAMBAT' :
                    infoPangkat.status === 'segera' ? 'SIAP!' : 'NORMAL'}
              </Badge>
            </HStack>

            <Divider mb={4} />

            {infoPangkat.status === 'no_data' ? (
              <Box textAlign="center" py={6} bg={bgInfo} borderRadius="md">
                <Icon as={InfoIcon} boxSize={12} color="blue.400" mb={2} />
                <Text color={textColorSecondary} fontSize="sm">
                  Data TMT Pangkat belum tersedia
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={textColorSecondary}>TMT Pangkat Terakhir:</Text>
                  <Text fontSize="sm" fontWeight="bold" color={textColorPrimary}>
                    {new Date(kepegawaian.tmt_pangkat_terakhir).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color={textColorSecondary}>Umur Jabatan:</Text>
                  <Badge colorScheme="blue" fontSize="sm">
                    {infoPangkat.umurJabatan}
                  </Badge>
                </HStack>

                {infoPangkat.adaHukdis && (
                  <Box bg="red.50" p={3} borderRadius="md" borderLeft="4px" borderColor="red.400">
                    <Text fontSize="xs" color="red.700" fontWeight="bold">
                      ⚠️ Ada Penundaan Hukdis
                    </Text>
                    <Text fontSize="xs" color="red.600" mt={1}>
                      {kepegawaian.hukdis} (+{infoPangkat.penundaanBulan} bulan)
                    </Text>
                  </Box>
                )}

                <HStack justify="space-between">
                  <Text fontSize="sm" color={textColorSecondary}>Deadline (4 tahun):</Text>
                  <Text fontSize="sm" fontWeight="bold" color="orange.600">
                    {infoPangkat.deadlineStr}
                  </Text>
                </HStack>

                <Divider />

                <Box
                  bg={infoPangkat.status === 'terlambat' ? 'red.50' :
                    infoPangkat.status === 'segera' ? 'green.50' : 'blue.50'}
                  p={4}
                  borderRadius="md"
                  textAlign="center"
                >
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={infoPangkat.status === 'terlambat' ? 'red.700' :
                      infoPangkat.status === 'segera' ? 'green.700' : 'blue.700'}
                    mb={2}
                  >
                    {infoPangkat.message}
                  </Text>
                  <Text
                    fontSize="md"
                    color={infoPangkat.status === 'terlambat' ? 'red.600' :
                      infoPangkat.status === 'segera' ? 'green.600' : 'blue.600'}
                    fontWeight="medium"
                    mb={2}
                  >
                    {infoPangkat.detail}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={infoPangkat.status === 'terlambat' ? 'red.600' :
                      infoPangkat.status === 'segera' ? 'green.600' : 'blue.600'}
                  >
                    {infoPangkat.actionMessage}
                  </Text>
                </Box>
              </VStack>
            )}
          </Card>
        </GridItem>

        {/* Card Kenaikan Gaji Berkala */}
        <GridItem>
          <Card
            p={6}
            border="2px solid"
            borderColor={infoGaji.status === 'terlambat' ? 'red.400' :
              infoGaji.status === 'segera' ? 'green.400' :
                infoGaji.status === 'belum_saatnya' ? 'blue.400' : borderColor}
          >
            <HStack justify="space-between" mb={4}>
              <HStack>
                <Icon as={CalendarIcon} boxSize={6} color="green.500" />
                <Text fontSize="lg" fontWeight="bold" color={textColorPrimary}>
                  Kenaikan Gaji Berkala
                </Text>
              </HStack>
              <Badge colorScheme={infoGaji.color} fontSize="sm" px={3} py={1}>
                <Icon as={infoGaji.icon} mr={1} />
                {infoGaji.status === 'no_data' ? 'NO DATA' :
                  infoGaji.status === 'terlambat' ? 'TERLAMBAT' :
                    infoGaji.status === 'segera' ? 'SIAP!' : 'NORMAL'}
              </Badge>
            </HStack>

            <Divider mb={4} />

            {infoGaji.status === 'no_data' ? (
              <Box textAlign="center" py={6} bg={bgInfo} borderRadius="md">
                <Icon as={InfoIcon} boxSize={12} color="blue.400" mb={2} />
                <Text color={textColorSecondary} fontSize="sm">
                  Data TMT Gaji belum tersedia
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={textColorSecondary}>TMT Gaji Terakhir:</Text>
                  <Text fontSize="sm" fontWeight="bold" color={textColorPrimary}>
                    {new Date(kepegawaian.tmt_gaji_terakhir).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color={textColorSecondary}>Umur Gaji:</Text>
                  <Badge colorScheme="blue" fontSize="sm">
                    {infoGaji.umurGaji}
                  </Badge>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color={textColorSecondary}>Deadline (2 tahun):</Text>
                  <Text fontSize="sm" fontWeight="bold" color="green.600">
                    {infoGaji.deadlineStr}
                  </Text>
                </HStack>

                <Divider />

                <Box
                  bg={infoGaji.status === 'terlambat' ? 'red.50' :
                    infoGaji.status === 'segera' ? 'green.50' : 'blue.50'}
                  p={4}
                  borderRadius="md"
                  textAlign="center"
                >
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={infoGaji.status === 'terlambat' ? 'red.700' :
                      infoGaji.status === 'segera' ? 'green.700' : 'blue.700'}
                    mb={2}
                  >
                    {infoGaji.message}
                  </Text>
                  <Text
                    fontSize="md"
                    color={infoGaji.status === 'terlambat' ? 'red.600' :
                      infoGaji.status === 'segera' ? 'green.600' : 'blue.600'}
                    fontWeight="medium"
                    mb={2}
                  >
                    {infoGaji.detail}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={infoGaji.status === 'terlambat' ? 'red.600' :
                      infoGaji.status === 'segera' ? 'green.600' : 'blue.600'}
                  >
                    {infoGaji.actionMessage}
                  </Text>
                </Box>
              </VStack>
            )}
          </Card>
        </GridItem>
      </Grid>

      {/* Info Footer */}
      <Card mt={4} p={4} bg={bgInfo}>
        <HStack>
          <Icon as={InfoIcon} color="blue.500" />
          <Text fontSize="sm" color={textColorSecondary}>
            <strong>Catatan:</strong> Jika Anda sudah memenuhi syarat kenaikan, segera hubungi atasan atau bagian SDM untuk memproses pengajuan.
          </Text>
        </HStack>
      </Card>
    </Box>
  );
}